/**
 * EconomicObservationModal Component
 *
 * Modal for creating economic note observations linked to tracked events.
 * This modal is for general notes only - slippage is handled via inline dropdown
 * in the TrackedEventCard.
 *
 * Features:
 * - Triggering event is always selected and cannot be deselected
 * - Can optionally link to any other tracked event in the session
 * - Free-form notes text (required to save)
 * - Validation: notes text required
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  TextArea,
  Alert,
  Card,
  CardHeader,
  CardBody,
} from '@enfomentis/ui';
import { formatTime } from '@/utils/dateHelpers';
import { noteDraftService } from '@/services/NoteDraftService';

// 3. Types
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';
import type { NoteDraft } from '@/types/transient/NoteDraft';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for EconomicObservationModal
 */
export interface EconomicObservationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when save succeeds (for parent to refresh data) */
  onSave?: () => void;
  /** The event that triggered opening this modal */
  triggeringEvent: EconomicEventDraft | null;
  /** All tracked events (for linking additional events) */
  trackedEvents: EconomicEventDraft[];
  /** User's display timezone for time formatting */
  displayTimezone: string;
  /** User's time format preference */
  timeFormat: '12h' | '24h';
  /** Observation being edited (null for new observation) */
  editingObservation?: NoteDraft | null;
}

// =============================================================================
// Component
// =============================================================================

/**
 * EconomicObservationModal Component
 */
export const EconomicObservationModal: React.FC<EconomicObservationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  triggeringEvent,
  trackedEvents,
  displayTimezone,
  timeFormat,
  editingObservation,
}) => {
  // Determine if we're in edit mode
  const isEditMode = editingObservation !== null && editingObservation !== undefined;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Selected event IDs (Set with triggering event always included) */
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());

  /** Notes text */
  const [notesText, setNotesText] = useState('');

  /** Saving state */
  const [isSaving, setIsSaving] = useState(false);

  /** Save error message */
  const [saveError, setSaveError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Derived Values
  // ---------------------------------------------------------------------------

  /**
   * Other tracked events: all tracked events except the triggering event.
   * Sorted by datetime, then alphabetically by name for stable UI ordering.
   */
  const otherTrackedEvents = useMemo(() => {
    if (!triggeringEvent) return [];

    return trackedEvents
      .filter((event) => event.id !== triggeringEvent.id)
      .sort((a, b) => {
        // First sort by datetime
        const dateA = a.datetime ?? '';
        const dateB = b.datetime ?? '';
        if (dateA !== dateB) {
          return dateA.localeCompare(dateB);
        }
        // Then by name
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [triggeringEvent, trackedEvents]);

  /**
   * Check if all other tracked events are selected
   */
  const allOtherSelected = useMemo(() => {
    if (otherTrackedEvents.length === 0) return true;
    return otherTrackedEvents.every((event) => selectedEventIds.has(event.id));
  }, [otherTrackedEvents, selectedEventIds]);

  /**
   * Validation: notes text is required
   */
  const isValid = useMemo(() => {
    return notesText.trim().length > 0;
  }, [notesText]);

  /**
   * Validation hint message
   */
  const validationHint = useMemo(() => {
    if (!isValid) {
      return 'Please add notes to save this observation.';
    }
    return null;
  }, [isValid]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /**
   * Reset state when modal opens (or populate from editingObservation for edit mode)
   */
  useEffect(() => {
    if (isOpen && triggeringEvent) {
      if (isEditMode && editingObservation) {
        // Edit mode: populate from existing observation
        // Always union owner into eventIds to prevent drop if eventIds is empty
        setSelectedEventIds(new Set([triggeringEvent.id, ...(editingObservation.eventIds ?? [])]));
        setNotesText(editingObservation.text ?? '');
      } else {
        // Add mode: reset to initial state with only triggering event selected
        setSelectedEventIds(new Set([triggeringEvent.id]));
        setNotesText('');
      }
      // Reset common state
      setIsSaving(false);
      setSaveError(null);
    }
  }, [isOpen, triggeringEvent, isEditMode, editingObservation]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  /**
   * Handle individual event checkbox change
   */
  const handleEventToggle = useCallback(
    (eventId: string, isSelected: boolean) => {
      // Cannot deselect triggering event
      if (eventId === triggeringEvent?.id) return;

      setSelectedEventIds((prev) => {
        const next = new Set(prev);
        if (isSelected) {
          next.add(eventId);
        } else {
          next.delete(eventId);
        }
        return next;
      });
    },
    [triggeringEvent]
  );

  /**
   * Handle "Select All" button click
   */
  const handleSelectAll = useCallback(() => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      for (const event of otherTrackedEvents) {
        next.add(event.id);
      }
      return next;
    });
  }, [otherTrackedEvents]);

  /**
   * Handle "Deselect All" button click
   */
  const handleDeselectAll = useCallback(() => {
    // Keep only triggering event
    if (triggeringEvent) {
      setSelectedEventIds(new Set([triggeringEvent.id]));
    }
  }, [triggeringEvent]);

  /**
   * Handle notes text change
   */
  const handleNotesChange = useCallback((value: string) => {
    setNotesText(value);
    setSaveError(null); // Clear error when user makes changes
  }, []);

  /**
   * Handle save (create new or update existing)
   */
  const handleSave = useCallback(async () => {
    if (!triggeringEvent || !isValid) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      if (isEditMode && editingObservation) {
        // Edit mode: update existing observation
        await noteDraftService.update(editingObservation.id, {
          text: notesText.trim(),
          eventIds: Array.from(selectedEventIds),
        });
      } else {
        // Add mode: create new observation
        await noteDraftService.add({
          noteType: 'economic',
          ownerId: triggeringEvent.id,
          timestamp: new Date().toISOString(),
          captureContext: 'pre-trading',
          text: notesText.trim(),
          eventIds: Array.from(selectedEventIds),
        });
      }

      // Success - call onSave callback (which handles close + refresh)
      onSave?.();
    } catch (error) {
      console.error('Failed to save observation:', error);
      setSaveError(
        error instanceof Error
          ? error.message
          : 'Failed to save observation. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [triggeringEvent, isValid, isEditMode, editingObservation, notesText, selectedEventIds, onSave]);

  // ---------------------------------------------------------------------------
  // Render Helpers
  // ---------------------------------------------------------------------------

  /**
   * Format event display for checkbox label
   */
  const formatEventLabel = (event: EconomicEventDraft): string => {
    let time = '--:--';
    if (event.datetime) {
      try {
        time = formatTime(event.datetime, displayTimezone, timeFormat);
      } catch {
        // Invalid datetime - use fallback
      }
    }
    const name = event.name || 'Untitled event';
    const country = event.country ? ` (${event.country})` : '';
    return `${time} - ${name}${country}`;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Guard: no triggering event
  if (!triggeringEvent) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" placement="center">
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Edit Economic Note' : 'Add Economic Note'}</ModalHeader>

        <ModalBody className="space-y-6">
          {/* Error Alert */}
          {saveError && (
            <Alert color="danger" variant="flat">
              {saveError}
            </Alert>
          )}

          {/* Notes Section */}
          <Card>
            <CardHeader className="pb-0">
              <h4 className="text-sm font-medium text-text-primary">Notes</h4>
            </CardHeader>
            <CardBody>
              <TextArea
                placeholder="Add observations about market conditions, expected impact, or trading considerations..."
                value={notesText}
                onValueChange={handleNotesChange}
                minRows={3}
                maxRows={6}
                classNames={{
                  input: 'text-base', // Prevents mobile zoom on focus
                }}
              />
            </CardBody>
          </Card>

          {/* Event Selection Section */}
          <Card>
            <CardHeader className="flex items-center justify-between pb-0">
              <h4 className="text-sm font-medium text-text-primary">Linked Events</h4>
              {otherTrackedEvents.length > 0 && (
                <Button
                  size="sm"
                  variant="light"
                  onPress={allOtherSelected ? handleDeselectAll : handleSelectAll}
                >
                  {allOtherSelected ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </CardHeader>
            <CardBody className="space-y-2">
              {/* Triggering/owner event (always selected, disabled) */}
              <Checkbox
                isSelected={true}
                isDisabled={true}
                classNames={{
                  label: 'text-sm',
                }}
              >
                {formatEventLabel(triggeringEvent)}
                <span className="text-xs text-text-tertiary ml-2">
                  {isEditMode ? '(owner event)' : '(triggering event)'}
                </span>
              </Checkbox>

              {/* Other tracked events */}
              {otherTrackedEvents.map((event) => (
                <Checkbox
                  key={event.id}
                  isSelected={selectedEventIds.has(event.id)}
                  onValueChange={(isSelected) => handleEventToggle(event.id, isSelected)}
                  classNames={{
                    label: 'text-sm',
                  }}
                >
                  {formatEventLabel(event)}
                </Checkbox>
              ))}

              {/* No other events message */}
              {otherTrackedEvents.length === 0 && (
                <p className="text-xs text-text-tertiary pl-7">
                  No other events tracked for this session
                </p>
              )}
            </CardBody>
          </Card>

          {/* Validation hint */}
          {validationHint && (
            <p className="text-xs text-text-tertiary">
              {validationHint}
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!isValid || isSaving}
            isLoading={isSaving}
          >
            Save Note
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EconomicObservationModal;
